import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  Settings,
  Video,
  MessageSquare,
  Brain,
  Bell,
  Link,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Eye,
  EyeOff,
  Copy,
  ExternalLink,
  Save,
  TestTube,
  FileText,
  Bot,
  BookOpen,
  Plus,
  Trash2,
  Edit,
  Send,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MeetingSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({ daily: false, whatsapp: false });
  const [showKeys, setShowKeys] = useState({ dailyApi: false, waToken: false });
  const [activeTab, setActiveTab] = useState('daily');

  // Form states
  const [dailySettings, setDailySettings] = useState({
    api_key: '',
    domain: '',
    auto_record: true,
    recording_type: 'cloud'
  });
  const [whatsappSettings, setWhatsappSettings] = useState({
    instance_id: '',
    api_token: '',
    enabled: true,
    default_chat_id: ''
  });
  const [aiSettings, setAiSettings] = useState({
    auto_transcribe: true,
    auto_analyze: true,
    auto_generate_mom: true,
    transcription_language: 'en',
    ai_model: 'gpt-4o'
  });
  const [notificationSettings, setNotificationSettings] = useState({
    whatsapp_notifications: true,
    email_notifications: false,
    slack_notifications: false,
    notification_template: 'default'
  });

  // WhatsApp MoM Template state
  const [momTemplate, setMomTemplate] = useState({
    template_name: 'default',
    header_text: '📋 *Meeting Minutes*',
    include_meeting_title: true,
    include_meeting_date: true,
    include_meeting_duration: true,
    include_attendees: true,
    include_attendee_emails: false,
    include_attendee_phones: false,
    include_summary: true,
    include_key_points: true,
    include_decisions: true,
    include_action_items: true,
    include_next_steps: true,
    include_recording_link: false,
    footer_text: '',
    max_summary_length: 500
  });
  const [momTemplatePreview, setMomTemplatePreview] = useState('');

  // WhatsApp Bot Settings state
  const [botSettings, setBotSettings] = useState({
    bot_name: 'Meeting Assistant',
    welcome_message: '👋 Hi! I\'m your Meeting Assistant. I can help you with:\n\n• Meeting summaries and MoM\n• Action items and tasks\n• Schedule information\n\nJust ask me anything!',
    default_response: 'I\'m not sure about that. Please try asking differently or contact support.',
    ai_enabled: true,
    ai_model: 'gpt-4o',
    ai_context: 'You are a helpful meeting assistant. You help users with meeting-related queries, summarize meetings, track action items, and provide information about past meetings.',
    response_language: 'en',
    auto_create_meeting: false,
    auto_assign_tasks: false
  });

  // Knowledge Base state
  const [knowledgeEntries, setKnowledgeEntries] = useState([]);
  const [showKnowledgeForm, setShowKnowledgeForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [newEntry, setNewEntry] = useState({
    category: 'faq',
    question: '',
    answer: '',
    keywords: '',
    priority: 0,
    enabled: true
  });

  // Test bot response state
  const [testMessage, setTestMessage] = useState('');
  const [testResponse, setTestResponse] = useState(null);
  const [testingBot, setTestingBot] = useState(false);

  const token = sessionStorage.getItem('auth_token');

  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/meeting-intelligence/settings?token=${token}`
      );
      const data = await response.json();
      if (data.success) {
        setSettings(data.settings);
        
        // Populate form states
        if (data.settings.daily_co) {
          setDailySettings(prev => ({
            ...prev,
            ...data.settings.daily_co,
            api_key: '' // Keep empty, show masked
          }));
        }
        if (data.settings.whatsapp) {
          setWhatsappSettings(prev => ({
            ...prev,
            ...data.settings.whatsapp,
            api_token: '' // Keep empty, show masked
          }));
        }
        if (data.settings.ai_processing) {
          setAiSettings(data.settings.ai_processing);
        }
        if (data.settings.notifications) {
          setNotificationSettings(data.settings.notifications);
        }
      }

      // Fetch MoM Template
      const momResponse = await fetch(
        `${API_URL}/api/meeting-intelligence/settings/whatsapp-mom-template?token=${token}`
      );
      const momData = await momResponse.json();
      if (momData.success && momData.template) {
        setMomTemplate(momData.template);
        setMomTemplatePreview(momData.template.preview || '');
      }

      // Fetch Bot Settings
      const botResponse = await fetch(
        `${API_URL}/api/meeting-intelligence/settings/whatsapp-bot?token=${token}`
      );
      const botData = await botResponse.json();
      if (botData.success && botData.settings) {
        setBotSettings(botData.settings);
      }

      // Fetch Knowledge Base
      const kbResponse = await fetch(
        `${API_URL}/api/meeting-intelligence/settings/whatsapp-bot/knowledge?token=${token}`
      );
      const kbData = await kbResponse.json();
      if (kbData.success) {
        setKnowledgeEntries(kbData.entries || []);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveSettings = async (section, data) => {
    setSaving(true);
    try {
      const endpoint = section === 'all' 
        ? '/settings' 
        : `/settings/${section}`;
      
      const response = await fetch(
        `${API_URL}/api/meeting-intelligence${endpoint}?token=${token}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        }
      );
      const result = await response.json();
      if (result.success) {
        toast.success(result.message || 'Settings saved successfully');
        fetchSettings(); // Refresh
      } else {
        toast.error(result.error || 'Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Save MoM Template
  const saveMomTemplate = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/meeting-intelligence/settings/whatsapp-mom-template?token=${token}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(momTemplate)
        }
      );
      const result = await response.json();
      if (result.success) {
        toast.success('MoM template saved successfully');
        fetchSettings();
      } else {
        toast.error(result.error || 'Failed to save template');
      }
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Save Bot Settings
  const saveBotSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/meeting-intelligence/settings/whatsapp-bot?token=${token}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(botSettings)
        }
      );
      const result = await response.json();
      if (result.success) {
        toast.success('Bot settings saved successfully');
        fetchSettings();
      } else {
        toast.error(result.error || 'Failed to save bot settings');
      }
    } catch (error) {
      toast.error('Failed to save bot settings');
    } finally {
      setSaving(false);
    }
  };

  // Add Knowledge Entry
  const addKnowledgeEntry = async () => {
    try {
      const entryData = {
        ...newEntry,
        keywords: newEntry.keywords.split(',').map(k => k.trim()).filter(Boolean)
      };
      
      const response = await fetch(
        `${API_URL}/api/meeting-intelligence/settings/whatsapp-bot/knowledge?token=${token}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(entryData)
        }
      );
      const result = await response.json();
      if (result.success) {
        toast.success('Knowledge entry added');
        setShowKnowledgeForm(false);
        setNewEntry({ category: 'faq', question: '', answer: '', keywords: '', priority: 0, enabled: true });
        fetchSettings();
      } else {
        toast.error(result.error || 'Failed to add entry');
      }
    } catch (error) {
      toast.error('Failed to add entry');
    }
  };

  // Update Knowledge Entry
  const updateKnowledgeEntry = async (entryId) => {
    try {
      const entryData = {
        ...editingEntry,
        keywords: typeof editingEntry.keywords === 'string' 
          ? editingEntry.keywords.split(',').map(k => k.trim()).filter(Boolean)
          : editingEntry.keywords
      };
      
      const response = await fetch(
        `${API_URL}/api/meeting-intelligence/settings/whatsapp-bot/knowledge/${entryId}?token=${token}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(entryData)
        }
      );
      const result = await response.json();
      if (result.success) {
        toast.success('Knowledge entry updated');
        setEditingEntry(null);
        fetchSettings();
      } else {
        toast.error(result.error || 'Failed to update entry');
      }
    } catch (error) {
      toast.error('Failed to update entry');
    }
  };

  // Delete Knowledge Entry
  const deleteKnowledgeEntry = async (entryId) => {
    if (!window.confirm('Delete this knowledge entry?')) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/meeting-intelligence/settings/whatsapp-bot/knowledge/${entryId}?token=${token}`,
        { method: 'DELETE' }
      );
      const result = await response.json();
      if (result.success) {
        toast.success('Entry deleted');
        fetchSettings();
      } else {
        toast.error(result.error || 'Failed to delete entry');
      }
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  // Test Bot Response
  const testBotResponse = async () => {
    if (!testMessage.trim()) {
      toast.error('Please enter a test message');
      return;
    }
    
    setTestingBot(true);
    setTestResponse(null);
    try {
      const response = await fetch(
        `${API_URL}/api/meeting-intelligence/settings/whatsapp-bot/test-response?token=${token}&message=${encodeURIComponent(testMessage)}`,
        { method: 'POST' }
      );
      const result = await response.json();
      if (result.success) {
        setTestResponse(result);
      } else {
        toast.error(result.error || 'Test failed');
      }
    } catch (error) {
      toast.error('Test failed');
    } finally {
      setTestingBot(false);
    }
  };

  const testConnection = async (service) => {
    setTesting(prev => ({ ...prev, [service]: true }));
    try {
      const response = await fetch(
        `${API_URL}/api/meeting-intelligence/settings/test-${service === 'daily' ? 'daily-co' : 'whatsapp'}?token=${token}`,
        { method: 'POST' }
      );
      const result = await response.json();
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.error || 'Connection test failed');
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setTesting(prev => ({ ...prev, [service]: false }));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="settings-loading">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const tabs = [
    { id: 'daily', label: 'Daily.co', icon: Video },
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
    { id: 'mom-template', label: 'MoM Template', icon: FileText },
    { id: 'bot-settings', label: 'Bot Settings', icon: Bot },
    { id: 'ai', label: 'AI Processing', icon: Brain },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'webhooks', label: 'Webhooks', icon: Link }
  ];

  return (
    <div className="space-y-6" data-testid="meeting-settings">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Meeting Intelligence Settings
          </h2>
          <p className="text-gray-500 text-sm">Configure APIs, credentials, and processing options</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <div className="flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Daily.co Settings */}
      {activeTab === 'daily' && (
        <Card data-testid="daily-settings">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Video className="h-5 w-5 text-blue-500" />
              Daily.co Video Conferencing
            </CardTitle>
            <CardDescription>
              Configure your Daily.co API for video meetings and recordings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Setup Instructions */}
            {settings?.setup_instructions?.daily_co && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">
                  {settings.setup_instructions.daily_co.title}
                </h4>
                <ol className="text-sm text-blue-700 space-y-1">
                  {settings.setup_instructions.daily_co.steps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
                <a
                  href={settings.setup_instructions.daily_co.docs_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 hover:underline mt-2 text-sm"
                >
                  View Documentation <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            )}

            {/* API Key */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API Key
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showKeys.dailyApi ? 'text' : 'password'}
                    value={dailySettings.api_key}
                    onChange={(e) => setDailySettings({ ...dailySettings, api_key: e.target.value })}
                    placeholder={settings?.daily_co?.api_key_masked || 'Enter Daily.co API Key'}
                    data-testid="daily-api-key-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKeys({ ...showKeys, dailyApi: !showKeys.dailyApi })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showKeys.dailyApi ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              {settings?.daily_co?.api_key_masked && (
                <p className="text-xs text-gray-500 mt-1">
                  Current: {settings.daily_co.api_key_masked}
                </p>
              )}
            </div>

            {/* Domain */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Domain
              </label>
              <Input
                value={dailySettings.domain}
                onChange={(e) => setDailySettings({ ...dailySettings, domain: e.target.value })}
                placeholder="yourcompany.daily.co"
                data-testid="daily-domain-input"
              />
            </div>

            {/* Options */}
            <div className="flex items-center justify-between">
              <div>
                <span className="font-medium">Auto-Record Meetings</span>
                <p className="text-sm text-gray-500">Automatically record when meeting starts</p>
              </div>
              <Switch
                checked={dailySettings.auto_record}
                onCheckedChange={(checked) => setDailySettings({ ...dailySettings, auto_record: checked })}
                data-testid="daily-auto-record-switch"
              />
            </div>

            {/* Recording Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Recording Type
              </label>
              <select
                value={dailySettings.recording_type}
                onChange={(e) => setDailySettings({ ...dailySettings, recording_type: e.target.value })}
                className="w-full h-10 px-3 border rounded-md"
                data-testid="daily-recording-type-select"
              >
                <option value="cloud">Cloud Recording</option>
                <option value="local">Local Recording</option>
              </select>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                onClick={() => saveSettings('daily-co', dailySettings)}
                disabled={saving}
                data-testid="save-daily-btn"
              >
                {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Daily.co Settings
              </Button>
              <Button
                variant="outline"
                onClick={() => testConnection('daily')}
                disabled={testing.daily}
                data-testid="test-daily-btn"
              >
                {testing.daily ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <TestTube className="h-4 w-4 mr-2" />}
                Test Connection
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* WhatsApp Settings */}
      {activeTab === 'whatsapp' && (
        <Card data-testid="whatsapp-settings">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-green-500" />
              WhatsApp Integration (Green-API)
            </CardTitle>
            <CardDescription>
              Configure WhatsApp for meeting notifications and MoM delivery
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Setup Instructions */}
            {settings?.setup_instructions?.whatsapp && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-medium text-green-800 mb-2">
                  {settings.setup_instructions.whatsapp.title}
                </h4>
                <ol className="text-sm text-green-700 space-y-1">
                  {settings.setup_instructions.whatsapp.steps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
                <a
                  href={settings.setup_instructions.whatsapp.docs_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-green-600 hover:underline mt-2 text-sm"
                >
                  View Documentation <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            )}

            {/* Enable Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <span className="font-medium">Enable WhatsApp</span>
                <p className="text-sm text-gray-500">Send meeting notifications via WhatsApp</p>
              </div>
              <Switch
                checked={whatsappSettings.enabled}
                onCheckedChange={(checked) => setWhatsappSettings({ ...whatsappSettings, enabled: checked })}
                data-testid="whatsapp-enabled-switch"
              />
            </div>

            {/* Instance ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instance ID
              </label>
              <Input
                value={whatsappSettings.instance_id}
                onChange={(e) => setWhatsappSettings({ ...whatsappSettings, instance_id: e.target.value })}
                placeholder="1234567890"
                data-testid="whatsapp-instance-input"
              />
            </div>

            {/* API Token */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API Token
              </label>
              <div className="relative">
                <Input
                  type={showKeys.waToken ? 'text' : 'password'}
                  value={whatsappSettings.api_token}
                  onChange={(e) => setWhatsappSettings({ ...whatsappSettings, api_token: e.target.value })}
                  placeholder={settings?.whatsapp?.api_token_masked || 'Enter API Token'}
                  data-testid="whatsapp-token-input"
                />
                <button
                  type="button"
                  onClick={() => setShowKeys({ ...showKeys, waToken: !showKeys.waToken })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showKeys.waToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {settings?.whatsapp?.api_token_masked && (
                <p className="text-xs text-gray-500 mt-1">
                  Current: {settings.whatsapp.api_token_masked}
                </p>
              )}
            </div>

            {/* Default Chat ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Group Chat ID
              </label>
              <Input
                value={whatsappSettings.default_chat_id}
                onChange={(e) => setWhatsappSettings({ ...whatsappSettings, default_chat_id: e.target.value })}
                placeholder="120363xxxxxx@g.us"
                data-testid="whatsapp-chat-id-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                WhatsApp group ID for default MoM delivery
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                onClick={() => saveSettings('whatsapp', whatsappSettings)}
                disabled={saving}
                data-testid="save-whatsapp-btn"
              >
                {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save WhatsApp Settings
              </Button>
              <Button
                variant="outline"
                onClick={() => testConnection('whatsapp')}
                disabled={testing.whatsapp}
                data-testid="test-whatsapp-btn"
              >
                {testing.whatsapp ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <TestTube className="h-4 w-4 mr-2" />}
                Test Connection
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* WhatsApp MoM Template Settings */}
      {activeTab === 'mom-template' && (
        <Card data-testid="mom-template-settings">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-500" />
              WhatsApp MoM Template
            </CardTitle>
            <CardDescription>
              Customize what information is included in the Minutes of Meeting sent via WhatsApp
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Header Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Header Text
              </label>
              <Input
                value={momTemplate.header_text}
                onChange={(e) => setMomTemplate({ ...momTemplate, header_text: e.target.value })}
                placeholder="📋 *Meeting Minutes*"
                data-testid="mom-header-input"
              />
              <p className="text-xs text-gray-500 mt-1">Use *text* for bold in WhatsApp</p>
            </div>

            {/* Meeting Info Toggles */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900">Meeting Information</h4>
              
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium">Include Meeting Title</span>
                </div>
                <Switch
                  checked={momTemplate.include_meeting_title}
                  onCheckedChange={(checked) => setMomTemplate({ ...momTemplate, include_meeting_title: checked })}
                  data-testid="mom-title-switch"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium">Include Meeting Date/Time</span>
                </div>
                <Switch
                  checked={momTemplate.include_meeting_date}
                  onCheckedChange={(checked) => setMomTemplate({ ...momTemplate, include_meeting_date: checked })}
                  data-testid="mom-date-switch"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium">Include Meeting Duration</span>
                  <p className="text-sm text-gray-500">Show how long the meeting lasted</p>
                </div>
                <Switch
                  checked={momTemplate.include_meeting_duration}
                  onCheckedChange={(checked) => setMomTemplate({ ...momTemplate, include_meeting_duration: checked })}
                  data-testid="mom-duration-switch"
                />
              </div>
            </div>

            {/* Attendee Toggles */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-medium text-gray-900">Attendee Information</h4>
              
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium">Include Attendee List</span>
                  <p className="text-sm text-gray-500">List of meeting participants</p>
                </div>
                <Switch
                  checked={momTemplate.include_attendees}
                  onCheckedChange={(checked) => setMomTemplate({ ...momTemplate, include_attendees: checked })}
                  data-testid="mom-attendees-switch"
                />
              </div>

              <div className="flex items-center justify-between pl-4">
                <div>
                  <span className="font-medium">Include Attendee Emails</span>
                  <p className="text-sm text-gray-500">Show email addresses next to names</p>
                </div>
                <Switch
                  checked={momTemplate.include_attendee_emails}
                  onCheckedChange={(checked) => setMomTemplate({ ...momTemplate, include_attendee_emails: checked })}
                  disabled={!momTemplate.include_attendees}
                  data-testid="mom-emails-switch"
                />
              </div>

              <div className="flex items-center justify-between pl-4">
                <div>
                  <span className="font-medium">Include Attendee Phone Numbers</span>
                  <p className="text-sm text-gray-500">Show phone numbers next to names</p>
                </div>
                <Switch
                  checked={momTemplate.include_attendee_phones}
                  onCheckedChange={(checked) => setMomTemplate({ ...momTemplate, include_attendee_phones: checked })}
                  disabled={!momTemplate.include_attendees}
                  data-testid="mom-phones-switch"
                />
              </div>
            </div>

            {/* Content Toggles */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-medium text-gray-900">Meeting Content</h4>
              
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium">Include Summary</span>
                  <p className="text-sm text-gray-500">Brief overview of the meeting</p>
                </div>
                <Switch
                  checked={momTemplate.include_summary}
                  onCheckedChange={(checked) => setMomTemplate({ ...momTemplate, include_summary: checked })}
                  data-testid="mom-summary-switch"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium">Include Key Points</span>
                  <p className="text-sm text-gray-500">Main discussion topics</p>
                </div>
                <Switch
                  checked={momTemplate.include_key_points}
                  onCheckedChange={(checked) => setMomTemplate({ ...momTemplate, include_key_points: checked })}
                  data-testid="mom-keypoints-switch"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium">Include Decisions</span>
                  <p className="text-sm text-gray-500">Decisions made during the meeting</p>
                </div>
                <Switch
                  checked={momTemplate.include_decisions}
                  onCheckedChange={(checked) => setMomTemplate({ ...momTemplate, include_decisions: checked })}
                  data-testid="mom-decisions-switch"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium">Include Action Items</span>
                  <p className="text-sm text-gray-500">Tasks assigned during the meeting</p>
                </div>
                <Switch
                  checked={momTemplate.include_action_items}
                  onCheckedChange={(checked) => setMomTemplate({ ...momTemplate, include_action_items: checked })}
                  data-testid="mom-actions-switch"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium">Include Next Steps</span>
                  <p className="text-sm text-gray-500">Follow-up actions and next meeting</p>
                </div>
                <Switch
                  checked={momTemplate.include_next_steps}
                  onCheckedChange={(checked) => setMomTemplate({ ...momTemplate, include_next_steps: checked })}
                  data-testid="mom-nextsteps-switch"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium">Include Recording Link</span>
                  <p className="text-sm text-gray-500">Link to meeting recording</p>
                </div>
                <Switch
                  checked={momTemplate.include_recording_link}
                  onCheckedChange={(checked) => setMomTemplate({ ...momTemplate, include_recording_link: checked })}
                  data-testid="mom-recording-switch"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="pt-4 border-t">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Footer Text (Optional)
              </label>
              <Textarea
                value={momTemplate.footer_text}
                onChange={(e) => setMomTemplate({ ...momTemplate, footer_text: e.target.value })}
                placeholder="Custom footer message..."
                rows={2}
                data-testid="mom-footer-input"
              />
            </div>

            {/* Max Summary Length */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Summary Length (characters)
              </label>
              <Input
                type="number"
                value={momTemplate.max_summary_length}
                onChange={(e) => setMomTemplate({ ...momTemplate, max_summary_length: parseInt(e.target.value) || 500 })}
                min={100}
                max={2000}
                data-testid="mom-maxlength-input"
              />
            </div>

            {/* Preview */}
            {momTemplatePreview && (
              <div className="pt-4 border-t">
                <h4 className="font-medium text-gray-900 mb-2">Preview</h4>
                <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm whitespace-pre-wrap">
                  {momTemplatePreview}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                onClick={saveMomTemplate}
                disabled={saving}
                data-testid="save-mom-template-btn"
              >
                {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save MoM Template
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* WhatsApp Bot Settings */}
      {activeTab === 'bot-settings' && (
        <div className="space-y-6" data-testid="bot-settings">
          {/* Bot Configuration Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5 text-violet-500" />
                WhatsApp Bot Configuration
              </CardTitle>
              <CardDescription>
                Configure bot behavior, messages, and AI settings for chat interactions
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Bot Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bot Name
                </label>
                <Input
                  value={botSettings.bot_name}
                  onChange={(e) => setBotSettings({ ...botSettings, bot_name: e.target.value })}
                  placeholder="Meeting Assistant"
                  data-testid="bot-name-input"
                />
              </div>

              {/* Welcome Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Welcome Message
                </label>
                <Textarea
                  value={botSettings.welcome_message}
                  onChange={(e) => setBotSettings({ ...botSettings, welcome_message: e.target.value })}
                  rows={4}
                  placeholder="Welcome message for new conversations..."
                  data-testid="bot-welcome-input"
                />
                <p className="text-xs text-gray-500 mt-1">Sent when a user starts a new conversation</p>
              </div>

              {/* Default Response */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Response
                </label>
                <Textarea
                  value={botSettings.default_response}
                  onChange={(e) => setBotSettings({ ...botSettings, default_response: e.target.value })}
                  rows={2}
                  placeholder="Response when no match is found..."
                  data-testid="bot-default-input"
                />
                <p className="text-xs text-gray-500 mt-1">Sent when bot doesn't understand the query</p>
              </div>

              {/* AI Settings */}
              <div className="pt-4 border-t space-y-4">
                <h4 className="font-medium text-gray-900 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-amber-500" />
                  AI-Powered Responses
                </h4>

                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium">Enable AI Responses</span>
                    <p className="text-sm text-gray-500">Use AI to answer queries not in knowledge base</p>
                  </div>
                  <Switch
                    checked={botSettings.ai_enabled}
                    onCheckedChange={(checked) => setBotSettings({ ...botSettings, ai_enabled: checked })}
                    data-testid="bot-ai-switch"
                  />
                </div>

                {botSettings.ai_enabled && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        AI Model
                      </label>
                      <select
                        value={botSettings.ai_model}
                        onChange={(e) => setBotSettings({ ...botSettings, ai_model: e.target.value })}
                        className="w-full h-10 px-3 border rounded-md"
                        data-testid="bot-model-select"
                      >
                        <option value="gpt-4o">GPT-4o (Recommended)</option>
                        <option value="gpt-4o-mini">GPT-4o Mini (Faster)</option>
                        <option value="gpt-5.2">GPT-5.2 (Latest)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        AI Context / System Prompt
                      </label>
                      <Textarea
                        value={botSettings.ai_context}
                        onChange={(e) => setBotSettings({ ...botSettings, ai_context: e.target.value })}
                        rows={3}
                        placeholder="Instructions for the AI bot..."
                        data-testid="bot-context-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Describe the bot's personality and capabilities</p>
                    </div>
                  </>
                )}
              </div>

              {/* Response Language */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Response Language
                </label>
                <select
                  value={botSettings.response_language}
                  onChange={(e) => setBotSettings({ ...botSettings, response_language: e.target.value })}
                  className="w-full h-10 px-3 border rounded-md"
                  data-testid="bot-language-select"
                >
                  <option value="en">English</option>
                  <option value="ar">Arabic</option>
                  <option value="hi">Hindi</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                </select>
              </div>

              {/* Automation Options */}
              <div className="pt-4 border-t space-y-4">
                <h4 className="font-medium text-gray-900">Automation Options</h4>

                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium">Auto-Create Meetings</span>
                    <p className="text-sm text-gray-500">Allow bot to create meetings from chat</p>
                  </div>
                  <Switch
                    checked={botSettings.auto_create_meeting}
                    onCheckedChange={(checked) => setBotSettings({ ...botSettings, auto_create_meeting: checked })}
                    data-testid="bot-autocreate-switch"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium">Auto-Assign Tasks</span>
                    <p className="text-sm text-gray-500">Allow bot to assign tasks from chat</p>
                  </div>
                  <Switch
                    checked={botSettings.auto_assign_tasks}
                    onCheckedChange={(checked) => setBotSettings({ ...botSettings, auto_assign_tasks: checked })}
                    data-testid="bot-autoassign-switch"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={saveBotSettings}
                  disabled={saving}
                  data-testid="save-bot-settings-btn"
                >
                  {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Bot Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Knowledge Base Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-cyan-500" />
                Knowledge Base
              </CardTitle>
              <CardDescription>
                Train your bot by adding questions and answers. The bot will use these to respond to similar queries.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add Entry Button */}
              <Button
                onClick={() => setShowKnowledgeForm(true)}
                variant="outline"
                className="w-full"
                data-testid="add-knowledge-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Knowledge Entry
              </Button>

              {/* Add Entry Form */}
              {showKnowledgeForm && (
                <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
                  <h4 className="font-medium">New Knowledge Entry</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={newEntry.category}
                      onChange={(e) => setNewEntry({ ...newEntry, category: e.target.value })}
                      className="w-full h-10 px-3 border rounded-md"
                      data-testid="new-entry-category"
                    >
                      <option value="faq">FAQ</option>
                      <option value="product">Product Info</option>
                      <option value="process">Process</option>
                      <option value="contact">Contact Info</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Question / Trigger</label>
                    <Input
                      value={newEntry.question}
                      onChange={(e) => setNewEntry({ ...newEntry, question: e.target.value })}
                      placeholder="What triggers this response?"
                      data-testid="new-entry-question"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Answer</label>
                    <Textarea
                      value={newEntry.answer}
                      onChange={(e) => setNewEntry({ ...newEntry, answer: e.target.value })}
                      placeholder="Bot response..."
                      rows={3}
                      data-testid="new-entry-answer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Keywords (comma-separated)</label>
                    <Input
                      value={newEntry.keywords}
                      onChange={(e) => setNewEntry({ ...newEntry, keywords: e.target.value })}
                      placeholder="meeting, schedule, help"
                      data-testid="new-entry-keywords"
                    />
                    <p className="text-xs text-gray-500 mt-1">Words that trigger this response</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority (0-10)</label>
                    <Input
                      type="number"
                      value={newEntry.priority}
                      onChange={(e) => setNewEntry({ ...newEntry, priority: parseInt(e.target.value) || 0 })}
                      min={0}
                      max={10}
                      data-testid="new-entry-priority"
                    />
                    <p className="text-xs text-gray-500 mt-1">Higher priority entries are checked first</p>
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={addKnowledgeEntry} data-testid="save-new-entry-btn">
                      <Save className="h-4 w-4 mr-2" />
                      Save Entry
                    </Button>
                    <Button variant="outline" onClick={() => setShowKnowledgeForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Existing Entries */}
              <div className="space-y-3">
                {knowledgeEntries.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <BookOpen className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No knowledge entries yet</p>
                    <p className="text-sm">Add entries to train your bot</p>
                  </div>
                ) : (
                  knowledgeEntries.map((entry) => (
                    <div key={entry.id} className="border rounded-lg p-4">
                      {editingEntry?.id === entry.id ? (
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                            <select
                              value={editingEntry.category}
                              onChange={(e) => setEditingEntry({ ...editingEntry, category: e.target.value })}
                              className="w-full h-10 px-3 border rounded-md"
                            >
                              <option value="faq">FAQ</option>
                              <option value="product">Product Info</option>
                              <option value="process">Process</option>
                              <option value="contact">Contact Info</option>
                              <option value="custom">Custom</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
                            <Input
                              value={editingEntry.question}
                              onChange={(e) => setEditingEntry({ ...editingEntry, question: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Answer</label>
                            <Textarea
                              value={editingEntry.answer}
                              onChange={(e) => setEditingEntry({ ...editingEntry, answer: e.target.value })}
                              rows={3}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Keywords</label>
                            <Input
                              value={Array.isArray(editingEntry.keywords) ? editingEntry.keywords.join(', ') : editingEntry.keywords}
                              onChange={(e) => setEditingEntry({ ...editingEntry, keywords: e.target.value })}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => updateKnowledgeEntry(entry.id)}>
                              <Save className="h-4 w-4 mr-1" />
                              Save
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingEntry(null)}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="text-xs">
                                  {entry.category}
                                </Badge>
                                {!entry.enabled && (
                                  <Badge variant="secondary" className="text-xs">
                                    Disabled
                                  </Badge>
                                )}
                              </div>
                              <p className="font-medium">{entry.question}</p>
                              <p className="text-sm text-gray-600 mt-1">{entry.answer}</p>
                              {entry.keywords?.length > 0 && (
                                <p className="text-xs text-gray-400 mt-2">
                                  Keywords: {entry.keywords.join(', ')}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-1 ml-2">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={() => setEditingEntry(entry)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-red-500"
                                onClick={() => deleteKnowledgeEntry(entry.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Test Bot Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TestTube className="h-5 w-5 text-orange-500" />
                Test Bot Response
              </CardTitle>
              <CardDescription>
                Test how the bot responds to different messages
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  placeholder="Type a test message..."
                  onKeyPress={(e) => e.key === 'Enter' && testBotResponse()}
                  data-testid="test-message-input"
                />
                <Button
                  onClick={testBotResponse}
                  disabled={testingBot}
                  data-testid="test-bot-btn"
                >
                  {testingBot ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>

              {testResponse && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant={testResponse.match_type === 'knowledge_base' ? 'default' : testResponse.match_type === 'ai_generated' ? 'secondary' : 'outline'}>
                      {testResponse.match_type === 'knowledge_base' && 'Knowledge Base Match'}
                      {testResponse.match_type === 'ai_generated' && 'AI Generated'}
                      {testResponse.match_type === 'default' && 'Default Response'}
                    </Badge>
                  </div>
                  <p className="text-gray-800 whitespace-pre-wrap">{testResponse.response}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* AI Processing Settings */}
      {activeTab === 'ai' && (
        <Card data-testid="ai-settings">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-500" />
              AI Processing Options
            </CardTitle>
            <CardDescription>
              Configure automatic transcription, analysis, and MoM generation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Auto Transcribe */}
            <div className="flex items-center justify-between">
              <div>
                <span className="font-medium">Auto-Transcribe</span>
                <p className="text-sm text-gray-500">Automatically transcribe recordings using Whisper</p>
              </div>
              <Switch
                checked={aiSettings.auto_transcribe}
                onCheckedChange={(checked) => setAiSettings({ ...aiSettings, auto_transcribe: checked })}
                data-testid="ai-auto-transcribe-switch"
              />
            </div>

            {/* Auto Analyze */}
            <div className="flex items-center justify-between">
              <div>
                <span className="font-medium">Auto-Analyze</span>
                <p className="text-sm text-gray-500">Automatically analyze transcript for insights</p>
              </div>
              <Switch
                checked={aiSettings.auto_analyze}
                onCheckedChange={(checked) => setAiSettings({ ...aiSettings, auto_analyze: checked })}
                data-testid="ai-auto-analyze-switch"
              />
            </div>

            {/* Auto Generate MoM */}
            <div className="flex items-center justify-between">
              <div>
                <span className="font-medium">Auto-Generate MoM</span>
                <p className="text-sm text-gray-500">Automatically generate Minutes of Meeting</p>
              </div>
              <Switch
                checked={aiSettings.auto_generate_mom}
                onCheckedChange={(checked) => setAiSettings({ ...aiSettings, auto_generate_mom: checked })}
                data-testid="ai-auto-mom-switch"
              />
            </div>

            {/* Transcription Language */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Transcription Language
              </label>
              <select
                value={aiSettings.transcription_language}
                onChange={(e) => setAiSettings({ ...aiSettings, transcription_language: e.target.value })}
                className="w-full h-10 px-3 border rounded-md"
                data-testid="ai-language-select"
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
                <option value="hi">Hindi</option>
                <option value="ar">Arabic</option>
                <option value="zh">Chinese</option>
                <option value="ja">Japanese</option>
              </select>
            </div>

            {/* AI Model */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                AI Model for Analysis
              </label>
              <select
                value={aiSettings.ai_model}
                onChange={(e) => setAiSettings({ ...aiSettings, ai_model: e.target.value })}
                className="w-full h-10 px-3 border rounded-md"
                data-testid="ai-model-select"
              >
                <option value="gpt-4o">GPT-4o (Recommended)</option>
                <option value="gpt-4o-mini">GPT-4o Mini (Faster)</option>
                <option value="gpt-5.2">GPT-5.2 (Latest)</option>
              </select>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                onClick={() => saveSettings('ai-processing', aiSettings)}
                disabled={saving}
                data-testid="save-ai-btn"
              >
                {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save AI Settings
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notification Settings */}
      {activeTab === 'notifications' && (
        <Card data-testid="notification-settings">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-orange-500" />
              Notification Preferences
            </CardTitle>
            <CardDescription>
              Configure how and where to send meeting notifications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* WhatsApp Notifications */}
            <div className="flex items-center justify-between">
              <div>
                <span className="font-medium">WhatsApp Notifications</span>
                <p className="text-sm text-gray-500">Send MoM to WhatsApp group</p>
              </div>
              <Switch
                checked={notificationSettings.whatsapp_notifications}
                onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, whatsapp_notifications: checked })}
                data-testid="notif-whatsapp-switch"
              />
            </div>

            {/* Email Notifications */}
            <div className="flex items-center justify-between">
              <div>
                <span className="font-medium">Email Notifications</span>
                <p className="text-sm text-gray-500">Send MoM via email to participants</p>
              </div>
              <Switch
                checked={notificationSettings.email_notifications}
                onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, email_notifications: checked })}
                data-testid="notif-email-switch"
              />
            </div>

            {/* Slack Notifications */}
            <div className="flex items-center justify-between">
              <div>
                <span className="font-medium">Slack Notifications</span>
                <p className="text-sm text-gray-500">Post MoM to Slack channel</p>
              </div>
              <Switch
                checked={notificationSettings.slack_notifications}
                onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, slack_notifications: checked })}
                data-testid="notif-slack-switch"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                onClick={() => saveSettings('notifications', notificationSettings)}
                disabled={saving}
                data-testid="save-notifications-btn"
              >
                {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Notification Settings
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Webhook URLs */}
      {activeTab === 'webhooks' && (
        <Card data-testid="webhook-settings">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link className="h-5 w-5 text-cyan-500" />
              Webhook Configuration
            </CardTitle>
            <CardDescription>
              Use these URLs to configure webhooks in your external services
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Daily.co Webhook */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Daily.co Webhook URL
              </label>
              <div className="flex gap-2">
                <Input
                  value={settings?.webhook_urls?.daily_co || ''}
                  readOnly
                  className="bg-gray-50"
                  data-testid="webhook-daily-url"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(settings?.webhook_urls?.daily_co)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Add this URL in Daily.co Dashboard → Webhooks
              </p>
            </div>

            {/* WhatsApp Webhook */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                WhatsApp (Green-API) Webhook URL
              </label>
              <div className="flex gap-2">
                <Input
                  value={settings?.webhook_urls?.whatsapp || ''}
                  readOnly
                  className="bg-gray-50"
                  data-testid="webhook-whatsapp-url"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(settings?.webhook_urls?.whatsapp)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Add this URL in Green-API Dashboard → Settings → Webhook URL
              </p>
            </div>

            {/* Teams Webhook */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Microsoft Teams Webhook URL
              </label>
              <div className="flex gap-2">
                <Input
                  value={settings?.webhook_urls?.teams || ''}
                  readOnly
                  className="bg-gray-50"
                  data-testid="webhook-teams-url"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(settings?.webhook_urls?.teams)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                (Future) For Microsoft Teams integration
              </p>
            </div>

            {/* Webhook Status */}
            <div className="bg-gray-50 rounded-lg p-4 mt-4">
              <h4 className="font-medium mb-3">Webhook Status</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Daily.co Webhooks</span>
                  <Badge variant="outline" className="flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                    Receiving
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">WhatsApp Webhooks</span>
                  <Badge variant="outline" className="flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                    Receiving
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MeetingSettings;
